import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

/**
 * Maps legacy `/vendors?tab=...&view=...` URLs to the new `/vendors/*` paths.
 * Mounted on the literal `/vendors` route only — never on a child route, so
 * the redirect can't loop on already-rewritten URLs.
 *
 * If no legacy params are present, returns `true` and lets the route's own
 * empty-path `redirectTo: 'inventory'` handle the default landing.
 */
export const legacyVendorsRedirectGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);

  // The path captured for matching this guard is `/vendors` (no children).
  // If the user navigated to a deeper path, the guard wouldn't be invoked
  // for the legacy-param mapping to fire; the new routes own those URLs.
  if (state.url.split('?')[0] !== '/vendors') {
    return true;
  }

  const tab = route.queryParamMap.get('tab');
  const view = route.queryParamMap.get('view');
  if (!tab && !view) {
    return true;
  }

  // Combined (tab, view) mapping. Unknown combinations fall back to the
  // canonical landing page (Inventory in the new IA).
  let segments: string[] = ['/vendors', 'inventory'];
  if (tab === 'customer') {
    if (view === 'relationships') segments = ['/vendors', 'inventory'];
    // Legacy "approvals" view is gone — usage approvals now live inline on
    // the inventory cards, so the closest equivalent landing is Inventory.
    else if (view === 'approvals') segments = ['/vendors', 'inventory'];
    else if (view === 'activities') segments = ['/vendors', 'activities'];
  } else if (tab === 'global') {
    // Legacy "global registry" landed on the registry tab. In the new IA the
    // global registry is the Add New Vendor browse page under Inventory.
    if (view === 'onboarding') segments = ['/vendors', 'inbox'];
    else segments = ['/vendors', 'inventory', 'add'];
  }

  return router.createUrlTree(segments);
};
